import { useEffect, useRef, useState } from "react";
import { Globe, ChevronDown } from "lucide-react";

const LANGS = [
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
  { code: "pl", label: "Polski" },
  { code: "pt", label: "Português" },
  { code: "it", label: "Italiano" },
  { code: "zh-CN", label: "中文" },
  { code: "ru", label: "Русский" },
  { code: "fr", label: "Français" },
  { code: "de", label: "Deutsch" },
  { code: "ar", label: "العربية" },
  { code: "ko", label: "한국어" },
  { code: "ja", label: "日本語" },
  { code: "hi", label: "हिन्दी" },
];

export default function LanguageSwitcher() {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("en");
  const wrapRef = useRef(null);

  // Load Google Translate once
  useEffect(() => {
    if (window.__gtLoaded) return;
    window.__gtLoaded = true;
    window.googleTranslateElementInit = () => {
      new window.google.translate.TranslateElement(
        { pageLanguage: "en", includedLanguages: LANGS.map((l) => l.code).join(","), autoDisplay: false },
        "google_translate_element"
      );
    };
    const s = document.createElement("script");
    s.src = "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
    s.async = true;
    document.body.appendChild(s);
  }, []);

  // Detect current language from cookie set by Google Translate
  useEffect(() => {
    const m = document.cookie.match(/googtrans=\/[^/]+\/([a-zA-Z-]+)/);
    if (m && m[1]) setCurrent(m[1]);
  }, []);

  // Click outside to close
  useEffect(() => {
    function onClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    if (open) document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const pick = (code) => {
    setCurrent(code);
    setOpen(false);
    // Set googtrans cookie for all hostnames and reload
    const hostname = window.location.hostname;
    const target = `/en/${code}`;
    const rootDomain = hostname.split(".").slice(-2).join(".");
    document.cookie = `googtrans=${target};path=/`;
    document.cookie = `googtrans=${target};domain=${hostname};path=/`;
    document.cookie = `googtrans=${target};domain=.${rootDomain};path=/`;
    window.location.reload();
  };

  const currentLabel = LANGS.find((l) => l.code === current)?.label || "English";

  return (
    <div className="relative" ref={wrapRef} data-testid="language-switcher">
      {/* Hidden Google Translate widget container */}
      <div id="google_translate_element" className="hidden" aria-hidden />

      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 px-3 py-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white text-sm font-heading tracking-wide transition-colors notranslate"
        translate="no"
        data-testid="lang-toggle"
        aria-label="Change language"
      >
        <Globe size={14} className="text-brand-gold" />
        <span className="hidden sm:inline">{currentLabel}</span>
        <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-52 max-h-80 overflow-auto bg-white text-brand-navy border-2 border-brand-navy shadow-[5px_5px_0_#D00017] z-50 notranslate"
          translate="no"
          data-testid="lang-menu"
        >
          {LANGS.map((l) => (
            <button
              key={l.code}
              onClick={() => pick(l.code)}
              className={`w-full text-left px-4 py-2 text-sm hover:bg-brand-red hover:text-white transition-colors ${
                current === l.code ? "bg-brand-navy text-white" : ""
              }`}
              data-testid={`lang-${l.code}`}
            >
              {l.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
